<template>
  <b-container fluid>
    <b-card
      border-variant="danger"
      :header="$t('EditCategories.header')"
      header-bg-variant="danger"
      header-text-variant="white"
      align="left"
    >
      <div>
        <b-tabs content-class="mt-3" v-model="tabIndex">
          <b-tab v-for="(lang, i) in langOptions" :key="i">
            <template v-slot:title>
              <country-flag :country="getLocale(lang)" size="normal" />
            </template>
          </b-tab>
        </b-tabs>
      </div>
      <b-form>
        <b-row class="my-1">
          <b-col>
            <b-form-select :options="dropDownList" required v-model="form.category"></b-form-select>
          </b-col>
        </b-row>
        <b-row class="mt-3" align-v="end">
          <b-col>
            <b-button
              :disabled="disabledBtn"
              variant="info"
              @click="editCategory"
            >{{ $t('EditCategories.edit') }}</b-button>
            <b-button
              :disabled="disabledBtn"
              variant="danger"
              @click="showModal"
            >{{ $t('EditCategories.delete') }}</b-button>
          </b-col>
          <b-col>
            <b-button
              variant="info"
              class="float-right"
              @click="addCategory"
            >{{ $t('EditCategories.add') }}</b-button>
          </b-col>
        </b-row>
        <b-row class="mt-3 justify-content-center">
          <b-alert
            :show="error.received"
            variant="danger"
            class="text-center"
            dismissible
          >{{error.msg}}</b-alert>
          <b-alert
            :show="success.received"
            variant="success"
            class="text-center"
            dismissible
          >{{success.msg}}</b-alert>
        </b-row>
      </b-form>
    </b-card>
    <!-- DELETE MODAL -->
    <b-modal ref="deleteModalRef" hide-footer header-bg-variant="danger">
      <div class="d-block text-center">
        <h4>{{ $t('EditCategories.modalConfirmation') }}</h4>
        <h3 v-if="selectedCategory">{{selectedCategory.title}}</h3>
      </div>
      <b-btn class="mt-3" cancel @click="hideModal">{{ $t('EditCategories.modalCancel') }}</b-btn>
      <b-btn
        class="mt-3"
        ok
        variant="danger"
        @click="deleteCategory"
      >{{ $t('EditCategories.modalDelete') }}</b-btn>
    </b-modal>
  </b-container>
</template>

<script>
/* CONTROLLER */
import CategoriesService from '@/services/CategoriesService'
import locales from '../i18n/locales'

export default {
  name: 'EditCategories',
  data () {
    return {
      categories: [],
      categoryName: [],
      dropDownList: [{
        value: null,
        text: this.$i18n.t('EditCategories.dropDownText')
      }],
      error: {
        received: false,
        msg: ''
      },
      form: {
        category: null
      },
      langOptions: [],
      selectedCategory: null,
      selectedLanguage: this.$i18n.locale,
      success: {
        received: false,
        msg: ''
      },
      tabIndex: 0,
      unfilteredCategories: []
    }
  },
  mounted () {
    // redirect if user not logged in
    let smartdineLocalStorage = JSON.parse(window.localStorage.getItem('smartdine'))

    if (!smartdineLocalStorage.token) this.$router.push({ name: 'Login' })

    // else, get categories and available languages
    else {
      this.getCategories()
      this.languageOptions()
      this.selectedLanguage = this.langOptions[this.tabIndex].value
    }
  },
  methods: {
    async getCategories () {
      const response = await CategoriesService.getCategories()
      this.unfilteredCategories = response.data.categories

      this.filterCategories()
    },
    filterCategories () {
      // clear out categories array if not empty
      if (this.categories.length > 0) {
        this.categories = []
      }

      // filter based on current language
      // and assign to categories
      this.unfilteredCategories.forEach((category) => {
        let localeCategory = {
          _id: category._id,
          title: this.filterLang(category),
          parent: category.parent || null,
          children: category.children || [],
          hidden: category.hidden,
          placeNumber: category.placeNumber || null
        }
        this.categories.push(localeCategory)
      })

      // clear out if list bigger than 1 item
      if (this.dropDownList.length > 1) {
        this.dropDownList = []
        this.dropDownList.push({ text: this.$i18n.t('EditCategories.dropDownText'), value: null })
      }

      // populate drop-down list
      this.categories.forEach(category => {
        let dplCategory = {
          text: category.title,
          value: category._id
        }

        this.dropDownList.push(dplCategory)
      })
    },
    getCategory () {
      this.selectedCategory = this.categories.find(category => {
        return category._id === this.form.category
      })
    },
    editCategory () {
      this.getCategory()

      // set currentCategory state
      let currentCategory = this.unfilteredCategories.filter(c => {
        return c._id === this.selectedCategory._id
      })[0]
      this.$store.dispatch('setCurrentCategory', currentCategory)

      this.$router.push({ name: 'EditCategory', params: { category: this.selectedCategory._id } })
    },
    addCategory () {
      this.$router.push({ name: 'AddCategory' })
    },
    showModal () {
      this.getCategory()
      this.$refs.deleteModalRef.show()
    },
    hideModal () {
      this.$refs.deleteModalRef.hide()
    },
    async deleteCategory () {
      try {
        const response = await CategoriesService.deleteCategory({ _id: this.selectedCategory._id })
        const deletedCategory = response.data.category

        // if has parent, delete from parent children array
        if (deletedCategory.parent) {
          try {
            const parent = await CategoriesService.getCategory(deletedCategory.parent)
            const childrenArr = parent.data.category.children.filter(child => child !== deletedCategory._id)
            const modifiedParent = {
              _id: parent.data.category._id,
              children: childrenArr
            }
            await CategoriesService.updateCategory(modifiedParent)
          } catch (error) {
            console.log(error)
          }
        }

        // if had children, delete from their parent field
        if (deletedCategory.children) {
          deletedCategory.children.forEach((child) => {
            this.deleteParent(child)
          })
        }

        // build success message
        this.success.received = true
        this.success.msg = `${this.filterLang(deletedCategory)} ${this.$i18n.t('EditCategories.serverMessages.deletedMessage')}`
      } catch (error) {
        this.error.received = true
        this.error.msg = error.response.data.error
      }
      // take out category from drop-down list and reset form
      this.dropDownList = this.dropDownList.filter(category => category.value !== this.selectedCategory._id)
      this.form.category = null

      this.hideModal()
    },
    async deleteParent (child) {
      let updatedChild = {
        _id: child,
        parent: null
      }
      try {
        await CategoriesService.updateCategory(updatedChild)
      } catch (error) {
        console.log(error)
      }
    },
    languageOptions () {
      // get languages from i18n and assign value/text to langOptions
      this.langOptions = Object.keys(this.$i18n.messages).map(l => {
        return { value: l, text: l.toUpperCase() }
      })
    },
    filterLang (category) {
      // filter for selected language
      let text = category.title.filter(t => { return t.lang === this.selectedLanguage })

      // if text value undefined, filter for and assing a defined value
      text = (text.length > 0) ? text[0].value : category.title.filter(t => { return t.value.length > 0 })[0].value
      return text
    },
    getLocale (lang) {
      return locales.filter(l => l.code === lang.value)[0].flag
    }
  },
  computed: {
    disabledBtn () {
      if (this.form.category === null) {
        return true
      } else {
        return false
      }
    }
  },
  watch: {
    tabIndex () {
      if (this.tabIndex !== -1) {
        this.selectedLanguage = this.langOptions[this.tabIndex].value
        this.filterCategories()
      }
    },
    selectedCategory () {
      this.categoryName = this.selectedCategory.title[0].value
    }
  }
}
</script>

<style scoped>
  .btn {
    margin: 0.1em 0.25em 0.1em 0.25em;
  }

  .modal-dialog h3 {
    color: #0d47a1;
  }
</style>



// WEBPACK FOOTER //
// src/components/EditCategories.vue